import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "src/context/Translations/context";
import Loader from "../Loader";
import IComponentInfiniteScroll from "./interface";
import { Container, ShowMoreButton } from "./styles";

const InfiniteScrollWithoutFetchInternal: React.FC<Omit<IComponentInfiniteScroll, "filter">> = ({
	mapper,
	listElements,
	defaultNumberOfElements = 40,
	manuallyToggleShowMore = false,
	startIndex = 0
}) => {
	const { translate } = useTranslation();
	const initialElements = startIndex + defaultNumberOfElements;
	const [elements, setElements] = useState<number>(initialElements);
	const [internalListElements, setInternalListElements] = useState(listElements.slice(0, elements));
	const [endScrolling, setEndScrolling] = useState(listElements.length <= elements); 
	const [loading, setLoading] = useState(false);

	const [mannuallyIncrementElements] = useState(manuallyToggleShowMore);

	const showMoreElements = useCallback(() => {
		setLoading(true);
		setElements((prevState) => prevState + defaultNumberOfElements);
	}, []);

	// When it scroll to the div(which was the last element in page) it increase the number of elements in page
	const observer = useRef<IntersectionObserver>();
	const infiniteScrollRef = useCallback((node) => {	
		if(observer.current)
			observer.current.disconnect();
		
		if(endScrolling)
			return;

		observer.current = new IntersectionObserver((entries) => {
			if(entries[0].isIntersecting && !endScrolling)
				showMoreElements();
		});

		if(node)
			observer.current.observe(node);
	}, [endScrolling]);

	useEffect(() => {
		setInternalListElements(listElements.slice(0, elements));
		setEndScrolling(listElements.length <= elements);
		setLoading(false);
	}, [elements, listElements]);

	return <>
		{internalListElements.map(mapper)}

		{!mannuallyIncrementElements ? (
			<div ref={infiniteScrollRef} />
		) : (
			<Container>
				{elements < listElements.length && (
					<ShowMoreButton type="button" onClick={showMoreElements}>
						{translate("Mostrar mais")}
					</ShowMoreButton>
				)}
			</Container>
		)}

		{loading && (<Loader />)}
	</>
	;
};

const InfiniteScrollWithoutFetch: React.FC<IComponentInfiniteScroll> = ({
	mapper,
	listElements,
	filter,
	defaultNumberOfElements = 40,
	manuallyToggleShowMore = false,
	startIndex = 0
}) => <InfiniteScrollWithoutFetchInternal 
	listElements={filter ? listElements.filter(filter) : listElements}
	mapper={mapper}
	defaultNumberOfElements={defaultNumberOfElements}
	manuallyToggleShowMore={manuallyToggleShowMore}
	startIndex={startIndex}
/>;

export default InfiniteScrollWithoutFetch;
